import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isBefore, startOfDay } from 'date-fns';
import { api } from '../../lib/api';
import { useUIStore } from '../../store/ui';
import { cn, formatPrice, getTrackColorClasses } from '../../lib/utils';

interface TrackType {
  id: string;
  name: string;
  slug: string;
  color: string;
  description?: string;
  pricePerSlot: number;
}

interface TimeSlot {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  trackTypeId: string;
}

interface AvailabilityCheck {
  available: boolean;
  reason?: string;
}

export function NewBooking() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { addToast } = useUIStore();

  // Form state
  const [step, setStep] = useState(1);
  const [tracks, setTracks] = useState<TrackType[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [selectedTrack, setSelectedTrack] = useState<TrackType | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [requestNotes, setRequestNotes] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Loading states
  const [loading, setLoading] = useState(true);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [availability, setAvailability] = useState<Record<string, AvailabilityCheck>>({});

  useEffect(() => {
    async function fetchTracks() {
      try {
        const res = await api.get('/api/public/tracks');
        if (res.tracks) {
          setTracks(res.tracks);
        }
      } catch (error) {
        console.error('Failed to fetch tracks:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchTracks();
  }, []);

  useEffect(() => {
    if (selectedTrack) {
      fetchTimeSlots(selectedTrack.id);
    }
  }, [selectedTrack]);

  useEffect(() => {
    if (selectedTrack && selectedDate) {
      checkAvailability();
    }
  }, [selectedTrack, selectedDate]);

  async function fetchTimeSlots(trackId: string) {
    try {
      const res = await api.get(`/api/public/tracks/${trackId}/time-slots`);
      if (res.timeSlots) {
        setTimeSlots(res.timeSlots);
      }
    } catch (error) {
      console.error('Failed to fetch time slots:', error);
    }
  }

  async function checkAvailability() {
    if (!selectedTrack || !selectedDate) return;

    setCheckingAvailability(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const res = await api.get(
        `/api/bookings/availability?trackId=${selectedTrack.id}&date=${dateStr}`
      );
      if (res.availability) {
        setAvailability(res.availability);
      }
    } catch (error) {
      console.error('Failed to check availability:', error);
    } finally {
      setCheckingAvailability(false);
    }
  }

  async function handleSubmit() {
    if (!selectedTrack || !selectedDate || !selectedSlot) return;

    setSubmitting(true);
    try {
      const res = await api.post(`/api/teams/${slug}/bookings`, {
        trackTypeId: selectedTrack.id,
        timeSlotId: selectedSlot.id,
        bookingDate: format(selectedDate, 'yyyy-MM-dd'),
        requestNotes,
      });

      if (res.booking) {
        addToast('Booking request submitted successfully!', 'success');
        navigate(`/team/${slug}/bookings`);
      }
    } catch (error) {
      addToast('Failed to submit booking request', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  // Calendar helpers
  const days = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  });
  const startDay = startOfMonth(currentMonth).getDay();
  const paddingDays = Array(startDay).fill(null);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">New Booking</h1>
        <p className="text-gray-600 mt-1">
          Request track time for your team.
        </p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between">
        {[
          { num: 1, label: 'Select Track' },
          { num: 2, label: 'Choose Date' },
          { num: 3, label: 'Pick Time Slot' },
          { num: 4, label: 'Confirm' },
        ].map((s, i) => (
          <div key={s.num} className="flex items-center">
            <div
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
                step >= s.num
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-200 text-gray-500'
              )}
            >
              {step > s.num ? <Check size={16} /> : s.num}
            </div>
            <span
              className={cn(
                'ml-2 text-sm hidden sm:inline',
                step >= s.num ? 'text-gray-900' : 'text-gray-500'
              )}
            >
              {s.label}
            </span>
            {i < 3 && (
              <div
                className={cn(
                  'w-12 sm:w-24 h-0.5 mx-2',
                  step > s.num ? 'bg-red-600' : 'bg-gray-200'
                )}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step 1: Select Track */}
      {step === 1 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Select a Track
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {tracks.map((track) => {
              const colors = getTrackColorClasses(track.color);
              const isSelected = selectedTrack?.id === track.id;

              return (
                <button
                  key={track.id}
                  onClick={() => {
                    setSelectedTrack(track);
                    setSelectedSlot(null);
                  }}
                  className={cn(
                    'p-4 rounded-xl border-2 text-left transition-all',
                    isSelected
                      ? `${colors.border} ${colors.light}`
                      : 'border-gray-200 hover:border-gray-300'
                  )}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className={cn('font-semibold', isSelected && colors.text)}>
                      {track.name}
                    </h3>
                    <div className={cn('w-3 h-3 rounded-full', colors.bg)} />
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{track.description}</p>
                  <div className="text-lg font-bold text-gray-900">
                    {formatPrice(track.pricePerSlot)}
                    <span className="text-sm font-normal text-gray-500">
                      {' '}
                      / slot
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="flex justify-end mt-6">
            <button
              onClick={() => setStep(2)}
              disabled={!selectedTrack}
              className="btn btn-primary"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Choose Date */}
      {step === 2 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Choose a Date
          </h2>

          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              className="btn btn-outline btn-sm"
            >
              <ChevronLeft size={18} />
            </button>
            <h3 className="text-lg font-semibold">
              {format(currentMonth, 'MMMM yyyy')}
            </h3>
            <button
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              className="btn btn-outline btn-sm"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div
                  key={day}
                  className="p-2 text-center text-sm font-semibold text-gray-600"
                >
                  {day}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {paddingDays.map((_, i) => (
                <div key={`pad-${i}`} className="h-12 bg-gray-50" />
              ))}
              {days.map((day) => {
                const isPast = isBefore(day, startOfDay(new Date()));
                const isSelected =
                  selectedDate &&
                  format(day, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');

                return (
                  <button
                    key={day.toISOString()}
                    disabled={isPast}
                    onClick={() => setSelectedDate(day)}
                    className={cn(
                      'h-12 flex items-center justify-center text-sm border-t border-r border-gray-200 transition-colors',
                      isPast && 'bg-gray-50 text-gray-300 cursor-not-allowed',
                      !isPast && !isSelected && 'hover:bg-gray-50',
                      isSelected && 'bg-red-600 text-white',
                      isToday(day) && !isSelected && 'font-bold text-red-600'
                    )}
                  >
                    {format(day, 'd')}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex justify-between mt-6">
            <button onClick={() => setStep(1)} className="btn btn-outline">
              Back
            </button>
            <button
              onClick={() => setStep(3)}
              disabled={!selectedDate}
              className="btn btn-primary"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Pick Time Slot */}
      {step === 3 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Select a Time Slot
          </h2>
          <p className="text-gray-600 mb-4">
            {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}
          </p>

          {checkingAvailability ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
            </div>
          ) : timeSlots.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="mx-auto text-gray-400 mb-3" size={40} />
              <p className="text-gray-600">
                No time slots available for this track.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {timeSlots.map((slot) => {
                const slotAvailability = availability[slot.id];
                const isAvailable = slotAvailability?.available !== false;
                const isSelected = selectedSlot?.id === slot.id;

                return (
                  <button
                    key={slot.id}
                    disabled={!isAvailable}
                    onClick={() => setSelectedSlot(slot)}
                    className={cn(
                      'w-full p-4 rounded-lg border-2 flex items-center justify-between transition-all',
                      !isAvailable && 'bg-gray-50 border-gray-200 cursor-not-allowed',
                      isAvailable && !isSelected && 'border-gray-200 hover:border-gray-300',
                      isSelected && 'border-red-600 bg-red-50'
                    )}
                  >
                    <div className="flex items-center space-x-3">
                      <Clock
                        size={20}
                        className={isSelected ? 'text-red-600' : 'text-gray-400'}
                      />
                      <div className="text-left">
                        <div className="font-medium text-gray-900">
                          {slot.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {slot.startTime} - {slot.endTime}
                        </div>
                      </div>
                    </div>
                    {!isAvailable && (
                      <span className="text-sm text-red-600">
                        {slotAvailability?.reason || 'Unavailable'}
                      </span>
                    )}
                    {isSelected && (
                      <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center">
                        <Check size={14} className="text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <div className="flex justify-between mt-6">
            <button onClick={() => setStep(2)} className="btn btn-outline">
              Back
            </button>
            <button
              onClick={() => setStep(4)}
              disabled={!selectedSlot}
              className="btn btn-primary"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Confirm */}
      {step === 4 && selectedTrack && selectedDate && selectedSlot && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Confirm Your Booking
          </h2>

          {/* Booking Summary */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3 mb-6">
            <div className="flex justify-between">
              <span className="text-gray-600">Track</span>
              <span className="font-medium text-gray-900">
                {selectedTrack.name}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Date</span>
              <span className="font-medium text-gray-900">
                {format(selectedDate, 'EEEE, MMMM d, yyyy')}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Time</span>
              <span className="font-medium text-gray-900">
                {selectedSlot.startTime} - {selectedSlot.endTime}
              </span>
            </div>
            <div className="border-t border-gray-200 pt-3 flex justify-between">
              <span className="text-gray-900 font-medium">Total</span>
              <span className="text-xl font-bold text-gray-900">
                {formatPrice(selectedTrack.pricePerSlot)}
              </span>
            </div>
          </div>

          {/* Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Additional Notes (optional)
            </label>
            <textarea
              value={requestNotes}
              onChange={(e) => setRequestNotes(e.target.value)}
              rows={3}
              className="input w-full"
              placeholder="Any special requests or information for the track manager..."
            />
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> This is a booking request. Our team will
              review your request and send you a confirmation with payment
              details.
            </p>
          </div>

          <div className="flex justify-between">
            <button onClick={() => setStep(3)} className="btn btn-outline">
              Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="btn btn-primary"
            >
              {submitting ? 'Submitting...' : 'Submit Booking Request'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
